import React, { createContext, useContext, useEffect, useState } from 'react';
import { OidcProviderProps } from './types/oidc-provider-props';
import { OidcContextProps } from './types/oidc-context-props';
import Claims from '../../types/claims';
import { OidcServiceFactory } from '../../factory/oidc-service-factory';
import { OidcService } from '../../types/oidc-service';
import { useAppState } from '@revenuewell/logic-app-state';
import { useProxy } from '@revenuewell/logic-wrapper-proxy';
import { useDesktop } from '@revenuewell/logic-desktop';
import { useConfig } from '@revenuewell/logic-config';

export const OidcContext = createContext<OidcContextProps>(null!);

export function OidcProvider(props: React.PropsWithChildren<OidcProviderProps>) {
  const { config } = useConfig();
  const { proxy } = useProxy();
  const { setCriticalError } = useAppState();
  const { isDesktopWidget, isDesktopApplication, sendMessage, clearCache } = useDesktop();
  const [ready, setReady] = useState<boolean>(false);
  const [oidcService, setOidcService] = useState<OidcService | null>();

  useEffect(() => {
    (async() => {
      try {
        const oidcServiceFactory = new OidcServiceFactory(props.oidcConfig, proxy, isDesktopWidget)
        const oidcService = await oidcServiceFactory.getOidcService()
        setOidcService(oidcService)
      } catch (error) {
        setCriticalError({ title: "OIDC Provider Critical Error", message: "Unable to initialize Oidc Service" })
      }
    })()
  }, [])

  useEffect(() => {
    if (oidcService) checkSession()
  }, [oidcService])

  const checkSession = async () => {
    if (props.checkSessionOnStart) {
      try {
        const authSessionValid = await oidcService?.checkSession()
        authSessionValid ? setReady(true) : oidcService?.signinRedirect();
      } catch(error) {
        setCriticalError({ title: "OIDC Provider Critical Error", message: "Unable to check Auth Session" });
      }
    } else {
      setReady(true)
    }
  }

  const context = {
    oidcService: oidcService,
    isAuthenticated: (): boolean => {
      return oidcService?.isAuthenticated() ?? false;
    },
    checkSession: () => {
      return oidcService?.checkSession();
    },
    getAccessToken: async (): Promise<string | null> => {
      return await oidcService?.getAccessToken() ?? null;
    },
    getClaims: async (): Promise<Claims | null> => {
      return await oidcService?.getClaims() ?? null;
    },
    signinRedirect: (): void => {
      oidcService?.signinRedirect();
    },
    forbiddenRedirect: (): void => {
      // todo: make a page inside front end UI
      //window.location.href = appConfig.oidc.authority + '/DefaultAuthentication/AccessDenied';
      throw new Error("Not implemented")
    },
    logoutRedirect: async (): Promise<void> => {
      if(isDesktopApplication() && !isDesktopWidget) {
        sendMessage({type: 'desktop-sign-out'})
      }

      const claims = await oidcService?.getClaims();

      if(claims?.userClass === 'CustomerUser') {
        // use Legacy Practice Portal sign out method instead oidcService?.logout()
        // to make sure Legacy Practice Portal session is cleared
        window.location.href = `${config.practice.url}/auth/signout`;
      } else {
        oidcService?.logout();
      }
    },
    signinRedirectCallback: async (): Promise<void> => {
      try {
        await oidcService?.signinRedirectCallback();
      } catch(error) {
        console.error('ODIC Sign In Error. ' + error)
        if(isDesktopApplication()) clearCache();
        await oidcService?.logout();
      }
    },
    onLogout: (handler: () => void) => {
      oidcService?.onLogout(handler);
    }
  } as OidcContextProps;

  return <OidcContext.Provider value={{ ...context }}>
    {ready && props.children}
  </OidcContext.Provider>;
}

export function useOidc() {
  const context = useContext(OidcContext);

  if (!context) {
    throw new Error('useOidc must be used within the OidcProvider');
  }

  return context;
}
